import { Octokit } from '@octokit/rest';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const GITHUB_CLIENT_ID = process.env.REACT_APP_GITHUB_CLIENT_ID;
const STORAGE_KEY = 'f2c-github-token';
const USERNAME_KEY = 'f2c-github-user';

/**
 * Get stored GitHub token from localStorage.
 */
export const getGitHubToken = () => {
  return localStorage.getItem(STORAGE_KEY);
};

/**
 * Get stored GitHub username.
 */
export const getGitHubUsername = () => {
  return localStorage.getItem(USERNAME_KEY);
};

/**
 * Check if GitHub is connected.
 */
export const isGitHubConnected = () => {
  return !!getGitHubToken();
};

/**
 * Redirect to GitHub OAuth consent page.
 * After user approves, GitHub redirects back with ?code=... in the URL.
 */
export const connectGitHub = () => {
  // Store current URL so we can restore it after OAuth callback
  sessionStorage.setItem('f2c-github-return', window.location.href);
  // Redirect to /projects (authenticated page) so the callback handler runs
  const redirectUri = window.location.origin + '/projects';
  const url = `https://github.com/login/oauth/authorize?client_id=${GITHUB_CLIENT_ID}&scope=public_repo&redirect_uri=${encodeURIComponent(redirectUri)}`;
  window.location.href = url;
};

/**
 * Handle the OAuth callback — exchange code for token via backend.
 * Call this on page load if ?code= is in the URL.
 */
export const handleOAuthCallback = async (code) => {
  const resp = await fetch(`${API_URL}/api/github/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    throw new Error(data.detail || `Token exchange failed (${resp.status})`);
  }
  const { access_token } = data;
  if (!access_token) throw new Error('No access token in response');
  localStorage.setItem(STORAGE_KEY, access_token);

  // Fetch and store username
  const octokit = new Octokit({ auth: access_token });
  const { data: user } = await octokit.users.getAuthenticated();
  localStorage.setItem(USERNAME_KEY, user.login);
  return user.login;
};

/**
 * Disconnect GitHub — clear stored token.
 */
export const disconnectGitHub = () => {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(USERNAME_KEY);
};

/**
 * Create an Octokit instance with the stored token.
 */
const getOctokit = () => {
  const token = getGitHubToken();
  if (!token) throw new Error('GitHub not connected. Please connect your GitHub account.');
  return new Octokit({ auth: token });
};

/**
 * List repos for authenticated user.
 */
export const listRepos = async () => {
  const octokit = getOctokit();
  const { data } = await octokit.repos.listForAuthenticatedUser({
    sort: 'updated',
    per_page: 50,
    affiliation: 'owner,collaborator',
  });
  return data;
};

/**
 * Create a new repository.
 */
export const createRepo = async (name, isPrivate = false) => {
  const octokit = getOctokit();
  const { data } = await octokit.repos.createForAuthenticatedUser({
    name,
    private: isPrivate,
    auto_init: true,
    description: 'F2C Academy solutions',
  });
  return data;
};

/**
 * List branches for a repo.
 */
export const listBranches = async (owner, repo) => {
  const octokit = getOctokit();
  const { data } = await octokit.repos.listBranches({
    owner,
    repo,
    per_page: 50,
  });
  return data;
};

/**
 * Create a new branch from the default branch.
 */
export const createBranch = async (owner, repo, branchName) => {
  const octokit = getOctokit();
  const { data: repoData } = await octokit.repos.get({ owner, repo });
  const defaultBranch = repoData.default_branch;
  const { data: refData } = await octokit.git.getRef({
    owner,
    repo,
    ref: `heads/${defaultBranch}`,
  });
  const sha = refData.object.sha;
  const { data } = await octokit.git.createRef({
    owner,
    repo,
    ref: `refs/heads/${branchName}`,
    sha,
  });
  return data;
};

/**
 * Push a single file to a repo (create or update).
 */
export const pushCode = async (owner, repo, branch, filePath, content, message) => {
  const octokit = getOctokit();
  let existingSha = null;
  try {
    const { data: existing } = await octokit.repos.getContent({
      owner,
      repo,
      path: filePath,
      ref: branch,
    });
    existingSha = existing.sha;
  } catch {
    // File doesn't exist yet
  }
  const { data } = await octokit.repos.createOrUpdateFileContents({
    owner,
    repo,
    path: filePath,
    message,
    content: btoa(unescape(encodeURIComponent(content))),
    branch,
    ...(existingSha ? { sha: existingSha } : {}),
  });
  return data;
};

/**
 * Push multiple files in a single commit using Git tree API.
 * files: [{ path: 'folder/file.ext', content: 'string content' }]
 */
export const pushMultipleFiles = async (owner, repo, branch, files, message) => {
  const octokit = getOctokit();

  // Get the latest commit SHA on the branch
  const { data: refData } = await octokit.git.getRef({
    owner, repo, ref: `heads/${branch}`,
  });
  const latestCommitSha = refData.object.sha;

  // Get the tree SHA of the latest commit
  const { data: commitData } = await octokit.git.getCommit({
    owner, repo, commit_sha: latestCommitSha,
  });
  const baseTreeSha = commitData.tree.sha;

  // Create blobs for each file
  const tree = await Promise.all(files.map(async (file) => {
    const { data: blob } = await octokit.git.createBlob({
      owner, repo,
      content: btoa(unescape(encodeURIComponent(file.content))),
      encoding: 'base64',
    });
    return {
      path: file.path,
      mode: '100644',
      type: 'blob',
      sha: blob.sha,
    };
  }));

  // Create a new tree
  const { data: newTree } = await octokit.git.createTree({
    owner, repo,
    base_tree: baseTreeSha,
    tree,
  });

  // Create a commit
  const { data: newCommit } = await octokit.git.createCommit({
    owner, repo,
    message,
    tree: newTree.sha,
    parents: [latestCommitSha],
  });

  // Update the branch reference
  await octokit.git.updateRef({
    owner, repo,
    ref: `heads/${branch}`,
    sha: newCommit.sha,
  });

  return newCommit;
};
